import {
	listSpaceTemplates,
	SharedSpaceTemplateListItemModel
} from "client/Api/Workspace/Templates/sharedSpaceTemplate";
import { Dialog, Toolbar } from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	EffectiveTheme,
	MediaQueryKind,
	useAlerts,
	useEffectiveTheme,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import styles from "./ChooseSpaceTemplateDialog.module.less";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { SpaceTemplateCard } from "./SpaceTemplateCard";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { never } from "client_legacy/ndaw/errors";

export type ChooseSpaceTemplateDialogProps = {
	onClose: () => void;
	onSelect: (templateId: number | null) => void;
	isBusy: boolean;
};

export const ChooseSpaceTemplateDialog = (props: ChooseSpaceTemplateDialogProps): ReactElement => {
	const {
		onClose,
		onSelect,
		isBusy: isBusyFromProps
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [selectedTemplate, setSelectedTemplate] = useState<OptionalModel<number | null>>({ isSet: false });
	const [templates, setTemplates] = useState<readonly SharedSpaceTemplateListItemModel[]>(EMPTY_ARRAY);

	const isPreviewAvailableInViewPort: boolean = useWorkspaceMediaQuery(MediaQueryKind.CompactTablet);
	const alertManager: AlertManager = useAlerts();
	const effectiveTheme: EffectiveTheme = useEffectiveTheme();

	const {
		currentItems
	} = dataSource(
		templates,
		{ sorting: sortByTemplateName }
	);

	useEffect(
		() => {
			const fetchTemplates = async (signal: AbortSignal) => {
				try {
					setIsBusy(true);

					const {
						items
					} = await listSpaceTemplates(signal);

					setTemplates(items);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			};

			const cancellation: AbortController = new AbortController();
			fetchTemplates(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[alertManager]
	);

	const submitHandler = useCallback(
		(): void => {
			if (!selectedTemplate.isSet) {
				throw new Error("No template is selected");
			}

			onSelect(selectedTemplate.value);
		},
		[onSelect, selectedTemplate]
	);

	const templateToPreview = useMemo(
		(): SharedSpaceTemplateListItemModel | null => {
			if (!selectedTemplate.isSet || isNil(selectedTemplate.value)) {
				return null;
			}

			const result: SharedSpaceTemplateListItemModel | undefined = currentItems.find(
				x => x.id === selectedTemplate.value
			);

			if (!result) {
				throw new Error(`Template was not found with id: ${selectedTemplate.value}`);
			}

			return result;
		},
		[currentItems, selectedTemplate]
	);

	return (
		<Dialog
			trackingId="choose-space-template-dialog"
			onClose={onClose}
			hasBusyIndicator={isBusy || isBusyFromProps}
			variant="left-slider"
		>
			<Dialog.Header
				title={localize("_SelectTemplateForSharedSpace")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<div className={styles.container}>
					<div className={styles.content}>

						<h3 className={styles.title}>
							{localize("_Templates")}
						</h3>

						<div className={styles.listContainer}>
							<List
								items={currentItems}
								effectiveTheme={effectiveTheme}
								selectedTemplate={selectedTemplate}
								onClick={(templateId) => setSelectedTemplate({ isSet: true, value: templateId })}
								onDoubleClick={onSelect}
							/>
						</div>
					</div>
					{isPreviewAvailableInViewPort && (
						<div className={styles.preview}>
							{templateToPreview && (
								<PreviewImage
									template={templateToPreview}
									effectiveTheme={effectiveTheme}
								/>
							)}
						</div>
					)}
				</div>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						trackingId="submit"
						text={localize("_Next")}
						kind="primary"
						onClick={submitHandler}
						isDisabled={
							isBusy
							|| isBusyFromProps
							|| !selectedTemplate.isSet
						}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ChooseSpaceTemplateDialog.displayName = "ChooseSpaceTemplateDialog";

type PreviewImageProps = {
	template: SharedSpaceTemplateListItemModel;
	effectiveTheme: EffectiveTheme;
};

const PreviewImage = (props: PreviewImageProps): ReactElement => {
	const {
		template,
		effectiveTheme
	} = props;

	const {
		name,
		previewImageUrl,
		darkThemePreviewImageUrl
	} = template;

	const imageUrl = (): string => {
		switch (effectiveTheme) {
			case Theme.Dark:
				return darkThemePreviewImageUrl;
			case Theme.Light:
				return previewImageUrl;
			default:
				never(effectiveTheme);
		}
	};

	return (
		<div className={styles.previewImageContainer}>
			<img
				className={styles.previewImage}
				src={imageUrl()}
				alt={name}
			/>
		</div>
	);
};
PreviewImage.displayName = "PreviewImage";

type ListProps = {
	effectiveTheme: EffectiveTheme;
	items: readonly SharedSpaceTemplateListItemModel[];
	selectedTemplate: OptionalModel<number | null>;
	onClick: (templateId: number | null) => void;
	onDoubleClick: (templateId: number | null) => void;
};

const List = (props: ListProps): ReactElement => {
	const {
		effectiveTheme,
		items,
		selectedTemplate,
		onClick,
		onDoubleClick
	} = props;

	return (
		<ul className={styles.list}>
			<li className={styles.listItem}>
				<SpaceTemplateCard
					trackingId="blank-template-card"
					name={localize("_BlankSpace")}
					description={localize("_StartFromScratchWithABlankSpaceDescription")}
					isSelected={
						selectedTemplate.isSet
							&& selectedTemplate.value === null
					}
					onClick={() => onClick(null)}
					onDoubleClick={() => onDoubleClick(null)}
				/>
			</li>
			{items.map(
				(x): ReactElement => (
					<li
						key={x.id}
						className={styles.listItem}
					>
						<SpaceTemplateCard
							trackingId={generateTrackingId("template-card", `|${x.id}`)}
							name={x.name}
							description={x.description}
							thumbnailUrl={getThumbnailUrl(x, effectiveTheme)}
							isSelected={
								selectedTemplate.isSet &&
									selectedTemplate.value === x.id
							}
							onClick={() => onClick(x.id)}
							onDoubleClick={() => onDoubleClick(x.id)}
						/>
					</li>
				)
			)}
		</ul>
	);
};
List.displayName = "List";

function getThumbnailUrl(
	template: SharedSpaceTemplateListItemModel,
	effectiveTheme: EffectiveTheme
): string {
	switch (effectiveTheme) {
		case Theme.Dark:
			return template.darkThemeThumbnailUrl;
		case Theme.Light:
			return template.thumbnailUrl;
		default:
			never(effectiveTheme);
	}
}

const sortByTemplateName: DataSourceSortableState<SharedSpaceTemplateListItemModel> = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "name"
};

const dataSource = createLocalDataSource<SharedSpaceTemplateListItemModel>()
	.setConfiguration({})
	.getHook();